/**
 * Centralized conversion / event tracking for chomp.vet.
 *
 * Every event pushed here has the SAME shape so GTM can build shared
 * variables (page_location, page_path, page_title, timestamp_iso, source).
 * Per-event extras (destination_url, phone_number, user_data, etc.) are
 * documented inline next to each helper.
 *
 * All events go to:
 *  1. `window.dataLayer` — read by GTM web container, sGTM, Meta CAPI,
 *     Harmonee, etc.
 *  2. `window.gtag()` — mirror to existing GA4 (G-S8EQ67P0DK) until GTM
 *     is the primary tag manager.
 *
 * PII handling: any user-identifying data (email, phone, name) is
 * SHA-256 hashed in the browser BEFORE being pushed to dataLayer, per
 * Google Enhanced Conversions and Meta Advanced Matching specs.
 * No raw PII ever enters dataLayer.
 */

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    // `gtag` is also declared in GoogleAnalytics.tsx; signature must match.
  }
}

/** Where on the site the event originated (button location). */
export type EventSource =
  | 'hero'
  | 'nav'
  | 'mobile_nav'
  | 'mobile_nav_top'
  | 'footer'
  | 'mobile_sticky_footer'
  | 'sticky_booking_bar'
  | 'final_cta'
  | 'notification_bar'
  | 'contact_page'
  | 'book_page'
  | 'service_page'
  | 'location_page'
  | 'blog_article'
  | 'dental_quiz'
  | 'cost_calculator'
  | 'our_team'
  | 'careers'
  | 'unknown';

const isBrowser = (): boolean => typeof window !== 'undefined';

/** Common params attached to every event for consistent GTM variables. */
const getCommonParams = (): Record<string, string> => {
  if (!isBrowser()) return {};
  return {
    page_location: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
    timestamp_iso: new Date().toISOString(),
  };
};

/**
 * Low-level: push an event to dataLayer + mirror to gtag.
 * Components should normally call one of the named helpers below instead.
 */
export const trackEvent = (
  eventName: string,
  params: Record<string, unknown> = {}
): void => {
  if (!isBrowser()) return;

  const payload = {
    event: eventName,
    ...getCommonParams(),
    ...params,
  };

  // 1. dataLayer push (GTM / sGTM / Meta CAPI / Harmonee will all pick this up).
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);

  // 2. Direct gtag mirror so existing GA4 keeps working before GTM is live.
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, payload);
  }
};

/* ------------------------------------------------------------------ */
/* PII hashing (Google Enhanced Conversions / Meta Advanced Matching) */
/* ------------------------------------------------------------------ */

/** SHA-256 hash, hex-encoded, lowercased & trimmed input. */
const sha256 = async (input: string): Promise<string> => {
  if (!isBrowser() || !window.crypto?.subtle) return '';
  const normalized = input.trim().toLowerCase();
  if (!normalized) return '';
  const buf = await window.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(normalized)
  );
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

/** Normalize phone to E.164 (US default) before hashing. */
const normalizePhone = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return `+${digits}`;
};

export interface HashedUserData {
  email_sha256?: string;
  phone_sha256?: string;
  first_name_sha256?: string;
  last_name_sha256?: string;
}

const hashUserData = async (raw: {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
}): Promise<HashedUserData> => {
  const [email_sha256, phone_sha256, first_name_sha256, last_name_sha256] =
    await Promise.all([
      raw.email ? sha256(raw.email) : Promise.resolve(''),
      raw.phone ? sha256(normalizePhone(raw.phone)) : Promise.resolve(''),
      raw.firstName ? sha256(raw.firstName) : Promise.resolve(''),
      raw.lastName ? sha256(raw.lastName) : Promise.resolve(''),
    ]);

  const out: HashedUserData = {};
  if (email_sha256) out.email_sha256 = email_sha256;
  if (phone_sha256) out.phone_sha256 = phone_sha256;
  if (first_name_sha256) out.first_name_sha256 = first_name_sha256;
  if (last_name_sha256) out.last_name_sha256 = last_name_sha256;
  return out;
};

/* ------------------------------------------------------------------ */
/* Named event helpers                                                */
/* ------------------------------------------------------------------ */

/**
 * Booking-portal outbound click (intent signal).
 * User clicked the "REQUEST A BOOKING" button that opens petportal.vet
 * in a NEW TAB. dataLayer.push is synchronous, so the event is committed
 * before the new tab is allocated by the browser — no risk of dropping.
 *
 * This does not confirm a submitted request or appointment. Keep the existing
 * event contract for GTM; verify received leads and bookings with the portal
 * before changing live advertising conversion settings.
 */
export const trackBookingOutbound = (
  source: EventSource = 'book_page',
  ctaText: string = 'REQUEST A BOOKING'
): void => {
  trackEvent('book_request_outbound', {
    source,
    destination_url: 'https://petportal.vet/chomp-pet-dental/appointment/request',
    cta_text: ctaText,
    conversion_type: 'lead',
  });
};

/**
 * User clicked any internal "Book Now" CTA that routes to /book.
 * Soft / intent signal — useful for funnel analysis and remarketing
 * audiences, NOT a primary conversion.
 */
export const trackBookingCtaClick = (
  source: EventSource,
  ctaText?: string
): void => {
  trackEvent('book_cta_click', {
    source,
    cta_text: ctaText ?? 'Book Now',
  });
};

/**
 * User tapped a tel: link. This does not confirm an answered or qualified call.
 */
export const trackPhoneClick = (
  source: EventSource,
  ctaText?: string
): void => {
  trackEvent('phone_call_click', {
    source,
    phone_number: '+19544663775',
    cta_text: ctaText ?? 'Call',
    conversion_type: 'lead',
  });
};

/** User clicked a mailto: link. Soft lead signal. */
export const trackEmailClick = (
  source: EventSource,
  email: string,
  ctaText?: string
): void => {
  trackEvent('email_click', {
    source,
    email_address: email,
    cta_text: ctaText ?? 'Email',
  });
};

/**
 * User successfully submitted the on-site contact form on /contact.
 * ONLY fires after server-side acknowledgement (Firestore save + email
 * send both succeed). Hard lead — wire as a secondary conversion.
 *
 * `user_data` is SHA-256 hashed in the browser (E.164 for phone,
 * lowercased/trimmed for everything) so it can be passed straight to
 * Google Enhanced Conversions / Meta Advanced Matching with no raw PII
 * touching the data layer.
 */
export const trackContactFormSubmit = async (raw: {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
}): Promise<void> => {
  const user_data = await hashUserData(raw);
  trackEvent('contact_form_submit', {
    source: 'contact_page',
    form_id: 'contact_us_form',
    conversion_type: 'lead',
    user_data,
  });
};

/**
 * Fires when the Dental Health Quiz RESULT screen renders (i.e. the user
 * answered all questions). Soft engagement / micro-conversion.
 */
export const trackDentalQuizComplete = (params: {
  score: number;
  maxScore: number;
  level: string;
  petType: 'dog' | 'cat' | null;
}): void => {
  trackEvent('dental_quiz_complete', {
    source: 'dental_quiz',
    quiz_id: 'dental_health_quiz',
    quiz_score: params.score,
    quiz_max_score: params.maxScore,
    quiz_score_percent: Math.round((params.score / params.maxScore) * 100),
    quiz_result_level: params.level,
    pet_type: params.petType ?? 'unknown',
  });
};

/**
 * Fires when the Cost Calculator RESULT screen renders (user finished all
 * 4 steps). Soft engagement / micro-conversion.
 */
export const trackCostCalculatorComplete = (params: {
  tier: 'routine' | 'moderate' | 'advanced';
  tierName: string;
  estimateLow: number;
  estimateHigh: number;
  petType: 'dog' | 'cat' | null;
}): void => {
  trackEvent('cost_calculator_complete', {
    source: 'cost_calculator',
    calculator_id: 'cost_calculator',
    estimate_tier: params.tier,
    estimate_tier_name: params.tierName,
    estimate_low: params.estimateLow,
    estimate_high: params.estimateHigh,
    estimate_avg: Math.round((params.estimateLow + params.estimateHigh) / 2),
    pet_type: params.petType ?? 'unknown',
  });
};

/** Generic outbound link tracker (e.g. Google Maps directions). */
export const trackOutboundClick = (url: string, source: EventSource): void => {
  trackEvent('outbound_click', { url, source });
};
